import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Popup8868 from '@/components/ui/8868Popup';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8868Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8868 Instructions: Tax Exempt Extension"
          description="File for tax exempt extension using Form 8868. Follow our instructions to request additional time for tax filing. Stay compliant and avoid penalties with ease."
          keywords="form 8868 instructions, irs form 8868 instructions, non-profit tax extension, instructions for form 8868, IRS tax extension form 8868, form 8868 filing instructions, form 990 extesnion, tax exempt extension"
          canonical="resources/tax-extension/form-8868-instructions/"
          ogType="article"
          jsonLdPricing="14.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Popup8868 />
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 8868</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File Form 8868 with TaxZerone to easily extend the filing deadline for
          your Form 990 series.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868instructions`}
          >
            Get Extension Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-8868-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8868 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Form 8868</span> is
                        filed by exempt organizations, charities, nonprofits,
                        and certain trusts with the IRS to request an
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          automatic 6-month extension
                        </span>{' '}
                        of time to file their returns.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <Link
                          href="/resources/tax-extension/form-8868-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>
                        : Form 8868 must be filed by the original due date of
                        the return, generally the{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          15th day of the 5th month
                        </span>{' '}
                        after the end of the organization's tax year (for
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          <br /> calendar-year
                        </span>{' '}
                        organizations, generally{' '}
                        <span style={{ fontWeight: 600 }}> May 15</span>).
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Penalties:</span>{' '}
                        Late payment penalty of{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          0.5% per month (up to 25%)
                        </span>{' '}
                        and applicable interest. Late filing penalty applies
                        unless you show a reasonable cause.
                      </span>
                    </li>
                  </Box>
                </Box>
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 8868?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Form 8868, Application for Automatic Extension of Time To
                      File an Exempt Organization Return is filed by charities,
                      nonprofit groups, and tax-exempt organizations to get an
                      extension to file their information return.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      By filing{' '}
                      <Link
                        href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        IRS Form 8868
                      </Link>
                      , exempt organizations can get up to 6 months of
                      extension. Also, they don’t need to provide a reason for
                      filing an extension.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 0, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 0.5,
                          lineHeight: 1.5,
                        }}
                      >
                        Need more time to file your 990 series forms?
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        E-File <b> Form 8868</b> with TaxZerone for up to
                        6-month extension – Use code{' '}
                        <Box
                          component="strong"
                          sx={{
                            ml: 0,
                            color: '#c02652',
                            fontWeight: 600,
                            border: '2px dashed rgb(211, 163, 33)',
                            px: 1,
                            backgroundColor: 'rgb(255, 250, 205)',
                          }}
                        >
                          TZOEXT10
                        </Box>{' '}
                        for <b>10% off </b> your filing today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868instructions`}
                      >
                        E-file 8868
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What’s New:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have access to U.S. banking services or electronic
                  payment systems, the IRS encourages you to receive refunds via
                  direct deposit and make any required payments electronically
                  whenever possible.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form 8868?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Exempt organizations that couldn’t file their annual
                  information return by the due date can file an extension Form
                  8868 and request 6 months extension of time.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The following information returns can be extended by filing
                  Form 8868
                </Typography>
                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <Link
                        href="/resources/tax-exempt/form-990-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 990
                      </Link>{' '}
                      or{' '}
                      <Link
                        href="/resources/tax-exempt/form-990-ez-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990-EZ
                      </Link>{' '}
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Form 4720 (individual)</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <Link
                        href="/resources/tax-exempt/form-990-pf-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990-PF
                      </Link>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Form 990-T (sec. 401(a) or 408(a) trust)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Form 990-T (trust other than above)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Form 990-T (corporation)</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Form 1041-A</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Form 4720 (other than individual)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <Link
                        href="/resources/tax-exempt/form-5227-instructions/ "
                        sx={{ color: '#0202ff' }}
                      >
                        Form 5227
                      </Link>{' '}
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Form 6069</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Form 8870</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Form 5330 (individual)</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Form 5330 (other than individual)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Form 990-T (governmental entities)
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Also, the trustee of a trust who is required to file Form
                  1041-A or Form 5227 can use 8868 extension form to request an
                  extension of time to file the returns.
                </Typography>

                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Form 8870 is available for{' '}
                  <span style={{ fontWeight: 600 }}> paper filing only</span>;
                  electronic filing is not permitted. See the mailing address
                  below.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 8868 due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for filing an extension Form 8868 will vary based
                  on when the organization’s accounting period ends.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For organizations that follow a fiscal tax year, Form 8868
                  must be filed by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    15th day of the 5th month
                  </span>{' '}
                  after your tax year ends.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the exempt organization follows the calendar tax year, the{' '}
                  <Link
                    href="/resources/tax-extension/form-8868-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    due date to file Form 8868
                  </Link>{' '}
                  is <span style={{ fontWeight: 600 }}>May 15, 2026.</span>{' '}
                  Filing Form 8868 for the 2025 tax year will extend the filing
                  deadline to November 15, 2026.
                </Typography>

                <Typography variant="body1" sx={{ mb: 5 }}>
                  However, if the due date falls on weekends or federal
                  holidays, then the filing deadline will be the next business
                  day.
                </Typography>

                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Need more time to file your 990 information return?
                      </Typography>
                      <Typography variant="body2">
                        E-file extension Form 8868 with the IRS using TaxZerone.
                        Complete the whole filing process by following 3 simple
                        steps in less than 5 minutes.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 8868 penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you miss filing your exempt organization return and paying
                  the tax due within the deadline, you will incur penalties from
                  the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Below are the penalty rates for 8868 form:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Failure to pay taxes and file tax returns
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Filed tax return or extension form, but failed to
                            pay taxes
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Late filing penalties differ based on the
                            information return for which you apply for an
                            extension.
                          </TableCell>
                          <TableCell>
                            0.5% of any tax due (other than estimated tax) for
                            each month or part of a month the tax is unpaid. The
                            maximum penalty is 25%.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 8868 Instructions - How to fill out?
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8868-part1-img.webp" />
                  <source srcSet="/assets/images/f8868-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8868-part1-img.png"
                    loading="lazy"
                    alt="2025 Form 8868 – Part I: Enter Name, TIN, and complete address"
                    width={760}
                    height={117}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To fill out the extension accurately, follow the Form 8868
                  instructions below
                </Typography>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Name of exempt organization or another filer:
                      </span>{' '}
                      The name of the filing organization must be entered. The
                      name entered should match the name on the IRS database.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> TIN:</span> Enter the
                      Taxpayer Identification Number
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Address:</span> Enter
                      your organization’s address. If the address is outside the
                      United States, in the space for “city or town, state, and
                      ZIP code,” enter the information in the following order:
                      city, province or state, and country.
                    </ListItemText>
                  </ListItem>
                </List>
                <picture>
                  <source srcSet="/assets/images/f8868-applicable-code-img.webp" />
                  <source srcSet="/assets/images/f8868-applicable-code-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8868-applicable-code-img.png"
                    loading="lazy"
                    alt="Enter the applicable Form 8868 return code"
                    width={760}
                    height={180}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Return Code based on the type of return for which
                  you are requesting a 8868 form extension. Enter only one
                  return code. If you want an extension for another form, you
                  must file a separate 8868 form for that return.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8868-form5330-img.webp" />
                  <source srcSet="/assets/images/f8868-form5330-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8868-form5330-img.png"
                    loading="lazy"
                    alt="File Form 5330: Plan Name, Plan Number, Plan year Ending"
                    width={760}
                    height={98}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      After entering your Return code, complete{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        either Part II or Part III,
                      </span>{' '}
                      but not both.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Filers of{' '}
                      <span style={{ fontWeight: 600 }}>Form 5330</span> must
                      first enter the{' '}
                      <span style={{ fontWeight: 600 }}>
                        plan name, plan number, and plan year ending
                      </span>{' '}
                      for the plan covered by this Form 8868 before completing
                      <span style={{ fontWeight: 600 }}> Part III.</span>
                    </ListItemText>
                  </ListItem>
                </List>
                <picture>
                  <source srcSet="/assets/images/f8868-part2-automatic-img.webp" />
                  <source srcSet="/assets/images/f8868-part2-automatic-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8868-part2-automatic-img.png"
                    loading="lazy"
                    alt="Form 8868 – Part II: Automatic Extension of Time to File for Exempt Organizations "
                    width={760}
                    height={132}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Enter the bookkeeper&apos;s details, telephone, and fax
                      number.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Check this box if the organization does not have an office
                      location or address in the United States.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      If this extension request is for a{' '}
                      <span style={{ fontWeight: 600 }}>group return,</span>{' '}
                      enter the organization’s four-digit{' '}
                      <span style={{ fontWeight: 600 }}>
                        Group Exemption Number (GEN).
                      </span>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Check the appropriate box to indicate whether the
                      extension applies to:
                      <List sx={{ mb: 0 }}>
                        <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                          <ListItemIcon
                            sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                          >
                            <DoubleArrowIcon />
                          </ListItemIcon>
                          <ListItemText>
                            <span style={{ fontWeight: 600 }}>
                              The Whole group,
                            </span>{' '}
                            or
                          </ListItemText>
                        </ListItem>
                        <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                          <ListItemIcon
                            sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                          >
                            <DoubleArrowIcon />
                          </ListItemIcon>
                          <ListItemText>
                            <span style={{ fontWeight: 600 }}>
                              Only part of the group.
                            </span>{' '}
                          </ListItemText>
                        </ListItem>
                      </List>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      If the extension applies to{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        part of the group,
                      </span>{' '}
                      attach a list that includes the{' '}
                      <span style={{ fontWeight: 600 }}>names and TINs</span> of
                      all members covered by the extension.
                    </ListItemText>
                  </ListItem>
                </List>
                <picture>
                  <source srcSet="/assets/images/f8868-tax-year-img.webp" />
                  <source srcSet="/assets/images/f8868-tax-year-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8868-tax-year-img.png"
                    loading="lazy"
                    alt="Form 8868 Part II: Lines 1 and 2 – Enter the tax year details and return type"
                    width={760}
                    height={146}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 1:</b> Select the tax year for which you request an
                  extension. If you follow a different tax year from the regular
                  calendar year, enter the same..
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 2:</b> Check the reason for the short tax year.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8868-part2-tentative-img.webp" />
                  <source srcSet="/assets/images/f8868-part2-tentative-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8868-part2-tentative-img.png"
                    loading="lazy"
                    alt="Form 8868 Part II: Lines 3a,3b and 3c – tentative tax, refundable credits and balance due"
                    width={760}
                    height={101}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 3a:</b> See the organization’s tax return and enter
                  the estimated amount of tentative tax reduced by any
                  nonrefundable credits. If you expect this amount to be zero,
                  enter -0-.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 3b:</b> See the organization’s tax return and enter
                  any refundable credits and estimated tax payments made. Also,
                  include any prior year overpayment allowed as a credit
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 4,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 3c:</b> Subtract line 3b from line 3a and enter the
                  balance due amount.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 8868?
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 400,
                    mb: 3,
                    lineHeight: 1.5,
                  }}
                >
                  Form 8868 can be filed with the IRS either electronically or
                  by paper filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic filing or E-filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing extension Form 8868 is an added advantage to you as
                  the process is quick and simple and you will get an instant
                  status of your return. You can{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 8868
                  </Link>{' '}
                  directly with the IRS or can opt to use an e-file service
                  provider.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  TaxZerone is an IRS-authorized e-file service provider that
                  supports the electronic filing of extension 8868 form. You can
                  simply enter the required information, calculate the balance
                  due, and transmit the return to the IRS.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Another method to file Form 8868 is paper filing. You can
                  manually fill out Form 8868 and send it via postal email.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you are paper filing your extension form, make sure you are
                  sending the return earlier before the deadline, as it may take
                  some time for the return to reach the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Although there are two filing options available for exempt
                  organizations to file an extension, the IRS recommends the
                  e-filing of 8868 form as it is simple, easy, and takes less
                  time and cost compared to paper filing.
                </Typography>

                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      E-filing extension Form 8868 with TaxZerone.
                    </Typography>
                    <Typography variant="body2">
                      Save your time, money, and effort by filing extension Form
                      8868 online with TaxZerone, an IRS-authorized e-file
                      service provider.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868instructions`}
                    >
                      E-File 8868 Now
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to mail Form 8868
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  After filling out all the required information in Form 8868,
                  you are required to manually send the return to the IRS by
                  postal mail.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Send a paper copy of 8868 form to the mailing address
                  mentioned below
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#c9f6d7',
                    minHeight: '100%',
                    p: 1.5,
                    maxWidth: 280,
                    textAlign: 'left',
                    justifyContent: 'center',
                    mb: 2,
                    boxShadow: ' 8px 8px rgb(28, 29, 28)',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    <b>Internal Revenue Service</b>
                    <br /> Mail Stop 6054
                    <br /> 1973 N Rulon White Blvd
                    <br />
                    Ogden, UT 84201–0045
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 5,
                    lineHeight: 1.5,
                  }}
                >
                  An application for extension of time to file Form 8870,
                  Information Return for Transfers Associated With Certain
                  Personal Benefit Contracts must be sent in paper format to the
                  address above.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 8868?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    lineHeight: 1.5,
                  }}
                >
                  E-filing Form 8868 can be done in 3 simple steps. Before you
                  start with the e-file process, keep the information below
                  ready.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Information required to file Form 8868
                </Typography>
                <List>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Exempt Organization or Filer information such as name and
                      address.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Organization&apos;s Tax Year</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Taxpayer Identification Number (TIN)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Return Code of the Application for which the organization
                      needs to file extension for 990 form
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Group Exemption Number (GEN) if filing extension for a
                      Group Return.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Tentative total tax</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Total payments and credits</ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Once you have all the information above, you can follow the
                  steps below to e-file Form 8868 using TaxZerone.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 1:</b> Enter your organization’s details, select the
                  tax year, and choose the appropriate tax form to file an
                  extension.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 2:</b> Calculate the balance due by providing
                  tentative total tax and total payments and credits
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 5,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 3:</b> Transmit the return to the IRS.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Running short on time to file your annual information
                        return?
                      </Typography>
                      <Typography variant="body2">
                        Quickly e-file extension Form 8868 with TaxZerone and
                        get an extension of up to 6 months.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to pay the balance due while filing Form 8868 online?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  While completing step 2, if you have a balance due, you can
                  choose to pay the balance due amount using the electronic
                  funds withdrawal (
                  <Link
                    href="/resources/electronic-funds-withdrawal-efw/"
                    sx={{ color: '#0202ff' }}
                  >
                    EFW
                  </Link>
                  ) option. You will need to provide your bank account
                  information, such as account number, account type, and routing
                  number.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Once the IRS processes your return, your balance due will be
                  automatically debited from your account. Please make sure to
                  maintain a sufficient balance in your account.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 8868 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File Form 8868 easily with TaxZerone at just <b>$14.99/return</b>.
              You will only have to enter the required information, calculate
              your balance due, and transmit the return to the IRS. The whole
              filing process gets completed in less than 5 minutes!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              You will also get email updates on your return status as soon as
              the IRS processes it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to file Form 8868 online?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8868instructions`}
              >
                E-file Form 8868 Now
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Get up to 6 months extension
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form8868Instructions;
